import {
  Box,
  Heading,
  Text,
  Skeleton,
  Flex,
  Grid,
  GridItem,
  Button,
} from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import {
  getSettingsPortfolioCovenantsBreadcrumb,
  SettingsBreadcrumb,
  SettingsFacilitiesBreadcrumb,
} from './SettingsBreadcrumbs';
import { colors } from 'packages/common-frontend/colors';
import { Breadcrumbs } from 'packages/common-frontend/components/Breadcrumbs';
import { useGetCreditFacilityQuery } from 'packages/credit-facility/requests/requests.gql.generated';

export type PortfolioCovenantEditTab =
  | 'properties'
  | 'inputs'
  | 'intermediateValues'
  | 'covenantValue';

const tabsToLabels: Record<PortfolioCovenantEditTab, string> = {
  properties: 'Properties',
  inputs: 'Inputs',
  intermediateValues: 'Intermediate Values',
  covenantValue: 'Covenant Value',
};

const PortfolioCovenantsSidebarMenu: React.FC<{
  selectedTab: PortfolioCovenantEditTab;
  setSelectedTab: React.Dispatch<
    React.SetStateAction<PortfolioCovenantEditTab>
  >;
}> = ({ selectedTab, setSelectedTab }) => {
  return (
    <Box
      padding="24px"
      borderWidth="1px"
      borderRadius="8px"
      borderColor={colors.gray[200]}
    >
      {_.map(tabsToLabels, (label, tab: PortfolioCovenantEditTab) => {
        return (
          <Button
            width="100%"
            variant="ghost"
            backgroundColor={
              tab === selectedTab ? colors.aurora[50] : undefined
            }
            fontSize="18px"
            fontWeight={600}
            onClick={() => setSelectedTab(tab)}
            mb="24px"
            p="8px 16px"
            justifyContent="flex-start"
            key={tab}
          >
            {label}
          </Button>
        );
      })}
    </Box>
  );
};

export const SettingsFacilityPortfolioCovenantsEditTemplate: React.FC<{
  creditFacilityId: string;
  selectedTab: PortfolioCovenantEditTab;
  setSelectedTab: React.Dispatch<
    React.SetStateAction<PortfolioCovenantEditTab>
  >;
  children: React.ReactNode;
}> = ({ creditFacilityId, selectedTab, setSelectedTab, children }) => {
  const {
    data: creditFacilityData,
    loading: isCreditFacilityLoading,
  } = useGetCreditFacilityQuery({ variables: { creditFacilityId } });

  if (!creditFacilityData || isCreditFacilityLoading) {
    return (
      <Skeleton
        h="600px"
        data-testid="settings-facility-portfolio-covenants-edit-template-loading"
      />
    );
  }

  const creditFacilityName = commonUtils.creditFacilitySlugToName(
    creditFacilityData.creditFacility.slug,
  );

  const breadcrumbs = [
    SettingsBreadcrumb,
    SettingsFacilitiesBreadcrumb,
    {
      label: creditFacilityName,
    },
    getSettingsPortfolioCovenantsBreadcrumb(creditFacilityId),
    {
      label: 'Edit',
    },
  ];

  return (
    <Box p="40px">
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <Box mt="16px">
        <Heading as="h1" mt="16px" mb="16px" fontSize="36px" fontWeight={600}>
          Portfolio Covenants
        </Heading>
        <Flex>
          <Text fontSize="16px" mb="16px" color={colors.black[70]}>
            Manage portfolio covenants for&nbsp;
          </Text>
          <Text fontSize="16px" mb="16px" fontWeight={500}>
            {`${creditFacilityName}.`}
          </Text>
        </Flex>
      </Box>
      <Box mt="64px">
        <Grid templateColumns="repeat(6, 1fr)">
          <GridItem colSpan={1}>
            <PortfolioCovenantsSidebarMenu
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
            />
          </GridItem>
          <GridItem colSpan={5} pl="40px">
            {children}
          </GridItem>
        </Grid>
      </Box>
    </Box>
  );
};
